import { FilterField } from "types/app";
import { isNullOrUndefined, cleanObj } from "./objectUtils";
import { NEGATE_FILTER, TIME_FRAMES } from "Consts";
import { convertUIDatesToMomentUserTzDates, getRanges } from "./dateUtils";

export const cleanFilterObj = (obj) => {
  if (!obj) return obj;

  for (const propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined) {
      delete obj[propName];
    }
  }
  return obj;
};

export const getFilterObjectByFieldsList = (
  globalTopFilterObject = {},
  filterFields: string[] | FilterField[] = [],
  filterAsArray = true
) => {
  const filterObj = {};

  if (filterFields.length == 0) return filterObj;

  Object.entries(globalTopFilterObject).forEach(([key, val]) => {
    //@ts-ignore
    const filterField: string | FilterField = filterFields
      .filter((item: string | FilterField | null | undefined) => !isNullOrUndefined(item))
      .find((item: string | FilterField) => {
        return typeof item == "string" ? item == key : item.field == key;
      });

    if (filterField && Array.isArray(val) && val.length > 0) {
      const filterName =
        typeof filterField === "string"
          ? filterField
          : filterField?.sendToServerName ?? filterField.field;

      if (filterAsArray && val.filter((i) => i !== null)?.length > 0) {
        filterObj[filterName] = val;
      }
      if (!filterAsArray) {
        filterObj[filterName] = val[0];
      }
    }
  });

  return filterObj;
};

export const getBoolFilter = (filtersObject = {}, aliasMap = {}) => {
  const globalFilters: any = { ...filtersObject };
  if (globalFilters?.tag) delete globalFilters.tag;

  const result = {
    must: {},
    mustNot: {},
  };

  Object.entries(cleanObj(globalFilters)).forEach(([key, arrValues]) => {
    const values = !Array.isArray(arrValues) ? [arrValues] : arrValues;

    const filterKey = aliasMap[key] ?? key;
    if (values.length) {
      values.forEach((val) => {
        if (`${val}`.length > 0) {
          if (`${val}`?.startsWith(NEGATE_FILTER)) {
            let valuesList = result.mustNot[filterKey];
            if (!valuesList) {
              result.mustNot[filterKey] = [];
              valuesList = result.mustNot[filterKey];
            }
            valuesList.push(`${val}`.replace(NEGATE_FILTER, ""));
          } else {
            let valuesList = result.must[filterKey];
            if (!valuesList) {
              result.must[filterKey] = [];
              valuesList = result.must[filterKey];
            }
            valuesList.push(val);
          }
        }
      });
    }
  });
  return result;
};

export const getV2Filter = (filtersObject: any = {}, aliasMap = {}) => {
  const result: Record<string, any> = {};
  const globalFilters: any = {
    ...filtersObject,
  };
  if (globalFilters?.tag) delete globalFilters.tag;

  Object.entries(cleanObj(globalFilters)).forEach(([key, arrValues]) => {
    const values = !Array.isArray(arrValues) ? [arrValues] : arrValues;
    const filterKey = aliasMap[key] ?? key;

    if (values?.length > 0) {
      values.forEach((val) => {
        if (`${val}`.length > 0) {
          if (`${val}`?.startsWith(NEGATE_FILTER)) {
            let valuesList = result[filterKey]?.excludeValues;
            if (!valuesList) {
              result[filterKey] = {
                ...result[filterKey],
                excludeValues: [],
              };
              valuesList = result[filterKey].excludeValues;
            }

            const VALUE = `${val}`.replace(NEGATE_FILTER, "");
            valuesList.push(VALUE);
          } else {
            let valuesList = result[filterKey]?.includeValues;
            if (!valuesList) {
              result[filterKey] = {
                ...result[filterKey],
                includeValues: [],
              };
              valuesList = result[filterKey]?.includeValues;
            }

            valuesList.push(val);
          }
        }
      });
    }
  });

  if (result.hasOwnProperty("category")) {
    result.jobCategory = result.category;
    delete result.category;
  }
  if (result.hasOwnProperty("job_name")) {
    result.jobName = result.job_name;
    delete result.job_name;
  }

  if (result.hasOwnProperty("alertName")) {
    result.alertType = {
      includeValues: result?.alertName?.includeValues?.map((i) => {
        const [alertTypeId, alertSubTypeId] = i?.split("_");
        return {
          alertTypeId: +alertTypeId,
          alertSubTypeId: isNullOrUndefined(alertSubTypeId) ? null : +alertSubTypeId,
        };
      }),
      excludeValues: result?.alertName?.excludeValues?.map((i) => {
        const [alertTypeId, alertSubTypeId] = i?.split("_");
        return {
          alertTypeId: +alertTypeId,
          alertSubTypeId: isNullOrUndefined(alertSubTypeId) ? null : +alertSubTypeId,
        };
      }),
    };
    result.alertName;

    delete result.alertName;
  }

  return Object.keys(result).length > 0 ? result : {};
};

const getTagFilterType = (filterFields) => {
  if (filterFields.includes("hosts")) return "Server";
  if (filterFields.includes("servername") && !filterFields.includes("databasename"))
    return "Server";
  if (filterFields.includes("servername") && filterFields.includes("databasename"))
    return "ServerAndDatabase";
  if (!filterFields.includes("servername") && filterFields.includes("databasename"))
    return "Database";
};

export const getTagFilter = (filterFields, tags) => {
  if (tags?.length > 0) {
    return {
      tagFilter: {
        filterType: getTagFilterType(filterFields),
        tags: tags?.map((i) => {
          const tag = i.split(":");
          return {
            name: tag[0].trim(),
            value: tag[1].trim(),
          };
        }),
      },
    };
  } else {
    return {};
  }
};

export const getV2TagFilter = (tags) => {
  return tags?.length > 0
    ? {
        tag: {
          includeTags: tags
            ?.filter((t) => !t.startsWith(NEGATE_FILTER))
            .map((i) => {
              const tag = i.split(":");
              return {
                name: tag[0].trim(),
                value: tag[1].trim(),
              };
            }),
          excludeTags: tags
            ?.filter((t) => t.startsWith(NEGATE_FILTER))
            .map((i) => {
              const excludeTag = i.split(NEGATE_FILTER)[1];
              const tag = excludeTag.split(":");
              return {
                name: tag[0].trim(),
                value: tag[1].trim(),
              };
            }),
        },
      }
    : {};
};

export const getViewsFilter = (filtersObject: any = {}, filterIds = {}) => {
  const result: Record<string, any> = {};
  const globalFilters: any = { ...filtersObject };
  const globalIds: any = { ...filterIds };

  if (globalFilters?.tag) delete globalFilters.tag;
  if (globalIds?.tag) delete globalIds.tag;

  Object.entries(cleanObj(globalFilters)).forEach(([key, arrValues]) => {
    const values = !Array.isArray(arrValues) ? [arrValues] : arrValues;
    const filterKey = key;

    if (values.length) {
      values.forEach((val) => {
        if (`${val}`.length > 0) {
          if (`${val}`?.startsWith(NEGATE_FILTER)) {
            let valuesList = result[filterKey]?.excludeValues;
            if (!valuesList) {
              result[filterKey] = {
                ...result[filterKey],
                excludeValues: [],
              };
              valuesList = result[filterKey].excludeValues;
            }
            valuesList.push(`${val}`.replace(NEGATE_FILTER, ""));
          } else {
            let valuesList = result[filterKey]?.includeValues;
            if (!valuesList) {
              result[filterKey] = {
                ...result[filterKey],
                includeValues: [],
              };
              valuesList = result[filterKey]?.includeValues;
            }
            valuesList.push(val);
          }
        }
      });
    }
  });

  if (Object.keys(globalIds)?.length > 0) {
    Object.entries(cleanObj(globalIds)).forEach(([key, arrValues]) => {
      const values = !Array.isArray(arrValues) ? [arrValues] : arrValues;
      const filterKey = key;

      if (values.length) {
        values.forEach((val) => {
          if (`${val}`.length > 0) {
            if (`${val}`?.startsWith(NEGATE_FILTER)) {
              let valuesList = result[filterKey]?.excludeIds;
              if (!valuesList) {
                result[filterKey] = {
                  ...result[filterKey],
                  excludeIds: [],
                };
                valuesList = result[filterKey].excludeIds;
              }

              const valToList = `${val}`.replace(NEGATE_FILTER, "");
              if (/^\d+$/.test(valToList)) valuesList.push(+valToList);
            } else {
              let valuesList = result[filterKey]?.includeIds;
              if (!valuesList) {
                result[filterKey] = {
                  ...result[filterKey],
                  includeIds: [],
                };
                valuesList = result[filterKey]?.includeIds;
              }
              if (/^\d+$/.test(val)) valuesList.push(+val);
            }
          }
        });
      }
    });
  }

  if (result.hasOwnProperty("category")) {
    result.jobCategory = result.category;
    delete result.category;
  }
  if (result.hasOwnProperty("job_name")) {
    result.jobName = result.job_name;
    delete result.job_name;
  }

  return Object.keys(result).length > 0 ? result : {};
};

export const isHourInRange = (start, end, hourString, globalTopFilterObject) => {
  const userTz = convertUIDatesToMomentUserTzDates(start, end, true);
  const hr: any = TIME_FRAMES.find((d) => d.name === hourString || d.short === hourString);

  // check also that it's not in global filter
  if (!isNullOrUndefined(globalTopFilterObject?.TimeOfDayWindowID)) {
    if (!globalTopFilterObject?.TimeOfDayWindowID?.includes(hr.id)) {
      return false;
    }
  }

  const arr = getRanges(userTz.start, userTz.end, hr);
  return arr.length > 0;
};

export const getYesNoFilter = (filterList) =>
  filterList?.map((i) => (i === "Yes" ? true : false)) || [];

export const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};
